import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, TextField, Button,
  Alert, Container, Link
} from '@mui/material';
import { Email as EmailIcon } from '@mui/icons-material';
import { authAPI } from '../services/api';
import { useNavigate, useLocation } from 'react-router-dom';

const EmailVerification = ({ onLogin }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email || '';
  const password = location.state?.password || '';

  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [resendTimer, setResendTimer] = useState(() => {
    const saved = localStorage.getItem(`resend_timer_${email}`);
    if (saved) {
      const { timestamp, duration } = JSON.parse(saved);
      const remaining = duration - Math.floor((Date.now() - timestamp) / 1000);
      return remaining > 0 ? remaining : 0;
    }
    return 60;
  });
  const [canResend, setCanResend] = useState(false);

  useEffect(() => {
    if (!email || !password) {
      navigate('/login', { replace: true });
      return;
    }
    if (!localStorage.getItem(`resend_timer_${email}`)) {
      setSuccess('Verification code sent! Check your email.');
    }
  }, []);

  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(r => r - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      setCanResend(true);
      localStorage.removeItem(`resend_timer_${email}`);
    }
  }, [resendTimer, email]);

  const handleVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await authAPI.verifyEmail({ email, code });
      await authAPI.confirmEmailVerification({ email });
      localStorage.removeItem(`resend_timer_${email}`);

      // Re-login — backend may still block for phone verification
      const loginResponse = await authAPI.login({ identifier: email, password });
      const { user, token, permissions, expires_at } = loginResponse.data;

      if (user.must_reset_password) {
        navigate('/first-time-reset', { state: { user, token, permissions, expires_at } });
      } else {
        onLogin({ user, token, permissions, expires_at });
      }
    } catch (err) {
      const data = err.response?.data;
      // If phone also needs verification, redirect there
      if (data?.requires_verification && data.method === 'phone') {
        navigate('/verify-phone', { state: { phone_number: data.phone_number, password } });
      } else {
        setError(data?.message || 'Verification failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (!canResend) return;
    setLoading(true);
    setError('');
    try {
      await authAPI.resendVerification({ email });
      setSuccess('Verification code sent! Check your email.');
      setResendTimer(60);
      setCanResend(false);
      localStorage.setItem(`resend_timer_${email}`, JSON.stringify({ timestamp: Date.now(), duration: 60 }));
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to resend code');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="100vh">
        <Card sx={{ width: '100%', maxWidth: 450 }}>
          <CardContent sx={{ p: 4 }}>
            <Box textAlign="center" mb={3}>
              <EmailIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
              <Typography variant="h4" gutterBottom>Verify Your Email</Typography>
              <Typography variant="body2" color="text.secondary">We've sent a verification code to</Typography>
              <Typography variant="body1" fontWeight="bold" color="primary">{email}</Typography>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

            <Box component="form" onSubmit={handleVerify}>
              <TextField
                fullWidth label="Verification Code" value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="Enter 6-digit code" required
                sx={{ mb: 2 }} inputProps={{ maxLength: 6 }}
              />
              <Button type="submit" fullWidth variant="contained"
                disabled={loading || code.length !== 6} sx={{ mb: 2 }}>
                {loading ? 'Verifying...' : 'Verify Email'}
              </Button>
              <Box textAlign="center">
                <Typography variant="body2" color="text.secondary">
                  Didn't receive the code?{' '}
                  {canResend
                    ? <Link component="button" type="button" onClick={handleResend} disabled={loading}>Resend</Link>
                    : <span style={{ color: '#999' }}>Resend in {resendTimer}s</span>}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};

export default EmailVerification;
